import { AttachInternals, Component, Element, h, Host, Prop } from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/sign-in?tab=designer).
 *
 * @slot default - Slot para o texto do sign-in.
 * @slot icon - Slot para ícone personalizado, com padrão fa6-solid:user para shape="circle".
 * @slot image - Slot para imagem ou ícone ao lado do texto.
 *
 * @part content - Parte para a área de conteúdo/texto do sign-in, usada para estilização via ::part().
 * @part image - Parte para a área da imagem/ícone, usada para controle de tamanho via CSS e ::part().
 */
@Component({
  tag: 'br-sign-in',
  styleUrl: 'sign-in.scss',
  shadow: true,
  formAssociated: true,
})
export class SignIn {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrSignInElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Define se o sign-in usará um esquema de cores escuro.
   */
  @Prop({ reflect: true }) readonly colorMode: 'dark'

  /**
   * Ajusta a densidade, alterando o espaçamento interno para um visual mais compacto ou mais expandido.
   */
  @Prop({ reflect: true }) readonly density: 'large' | 'medium' | 'small' = 'medium'

  /**
   * Desativa o sign-in, tornando-o não interativo.
   */
  @Prop({ reflect: true }) readonly disabled: boolean = false

  /**
   * Define a ênfase do sign-in, alterando sua aparência para criar hierarquia visual.
   */
  @Prop({ reflect: true }) readonly emphasis: 'primary' | 'secondary' = 'secondary'

  /**
   * Define o formato do sign-in.
   */
  @Prop({ reflect: true }) readonly shape: 'block' | 'circle' | 'pill' = 'pill'

  /**
   * URL para onde o usuário será redirecionado ao clicar. Use para login com provedores externos ou em sites tradicionais.
   */
  @Prop({ reflect: true }) readonly href: string

  /**
   * Define onde abrir o link quando href é fornecido.
   */
  @Prop({ reflect: true }) readonly target: '_self' | '_blank' | '_parent' | '_top' = '_self'

  /**
   * Define o rótulo acessível do sign-in, especialmente importante para formatos circulares.
   */
  @Prop({ reflect: true }) readonly ariaLabel: string

  private readonly handleClick = (event: MouseEvent) => {
    if (this.disabled) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
  }

  private readonly handleFocus = (event: FocusEvent) => {
    if (this.disabled) {
      ;(event.target as HTMLElement)?.blur?.()
    }
  }

  private hasSlotContent(slotName: string): boolean {
    return this.el.querySelector(`[slot="${slotName}"]`) !== null
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-sign-in': true,
      primary: this.emphasis === 'primary',
      secondary: this.emphasis === 'secondary',
      large: this.density === 'large',
      small: this.density === 'small',
      block: this.shape === 'block',
      circle: this.shape === 'circle',
      'dark-mode': this.colorMode === 'dark',
      disabled: this.disabled,
    }
  }

  private getTagType(): string {
    return this.href && this.href.length > 0 ? 'a' : 'button'
  }

  private getAttributes() {
    const TagType = this.getTagType()

    if (TagType === 'a') {
      return {
        href: this.disabled ? null : this.href,
        target: this.disabled ? null : this.target,
        'aria-disabled': this.disabled ? 'true' : null,
        'aria-label': this.ariaLabel,
      }
    }

    return {
      type: 'button',
      disabled: this.disabled,
      'aria-label': this.ariaLabel,
    }
  }

  render() {
    const TagType = this.getTagType()
    const attributes = this.getAttributes()
    const classList = this.getCssClassMap()

    return (
      <Host aria-disabled={this.disabled ? 'true' : null}>
        <TagType
          part="button"
          class={classList}
          id={this.customId}
          {...attributes}
          onClick={this.handleClick}
          onFocus={this.handleFocus}
        >
          <slot name="icon">{this.shape === 'circle' && <br-icon icon-name="fa6-solid:user"></br-icon>}</slot>
          <span part="content">
            <slot></slot>
          </span>
          {this.hasSlotContent('image') && (
            <span part="image">
              <slot name="image"></slot>
            </span>
          )}
        </TagType>
      </Host>
    )
  }
}
